import { normalizeWorkerIdString } from '../../utils/workerAgentIds';
import type { StateCreator } from 'zustand';
import type { OptimizationState } from '../optimizationStore';
import {
  WorkerAgentConfig,
  ChatMessage,
  cloneWorkers,
  DEFAULT_COORDINATOR_DESCRIPTION,
  INITIAL_WORKERS,
} from '../storeShared';

/** Connector multi-agent configurator: its own coordinator/workers, run and chat. (AIT-423) */
export interface ConnectorSlice {
  // Конфигурация для ConnectorPage (отдельная от MultiAgentPage)
  connectorWorkers: WorkerAgentConfig[];
  connectorCoordinatorPrompt: string;
  connectorCoordinatorModel: string;
  connectorCoordinatorTemperature: number;
  connectorCoordinatorDescription: string;
  connectorCoordinatorReminderPrompt: string;
  /** Coordinator display name from Integration Configurator */
  connectorCoordinatorName: string;
  /** Coordinator backend id from integration config (UUID). */
  connectorCoordinatorBackendId?: string;
  /** Coordinator agent_id when distinct from id. */
  connectorCoordinatorAgentId?: string;
  /** Placement of reminder text in connector optimization config (default before). */
  connectorReminderPromptPosition: 'before' | 'after';
  connectorConfigLabel?: string;
  connectorMetaPrompts?: {
    CORE_PROMPT?: string;
    MASTER_SUFFIX?: string;
    ASYNC_SUFFIX?: string;
    SYNC_EXTRA?: string;
    RAG_PROMPT?: string;
    LTM_PROMPT?: string;
    MODERATION_PROMPT?: string;
    DIARY_WRITE_PROMPT?: string;
    DIARY_READ_PROMPT?: string;
    PORTRAIT_READ_PROMPT?: string;
    PORTRAIT_WRITE_PROMPT?: string;
    FINAL_STEP_PROMPT?: string;
  };

  // Мультиагентные результаты оптимизации для ConnectorPage (отдельные от MultiAgentPage)
  connectorMultiAgentOptimizedPrompts: string[];
  connectorMultiAgentIterationResults: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number;
    temperature?: number;
    'master-agent'?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    'worker-agents'?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      mode?: 'synchronous' | 'asynchronous';
      reminder_prompt?: string;
    }>;
    meta_prompts?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
    metaPrompts?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
  }>;
  connectorMultiAgentIsOptimized: boolean;
  connectorMultiAgentSelectedIterationIndex: number;
  connectorMultiAgentSelectedPrompt: string;
  connectorMultiAgentSelectedPromptIndex: string;
  connectorMultiAgentGraphSliderValue: number;
  connectorMultiAgentIsOptimizing: boolean;
  connectorMultiAgentOptimizationProgress: number;

  // Экспоненциальный прогресс-бар для connector multi-agent
  connectorMultiAgentProgressStartTime: number | null;
  connectorMultiAgentCurrentProgressStage: number;
  connectorMultiAgentExpectedIterationTime: number;

  // Чат для Connector Multi-Agent
  connectorMultiAgentChatMessages: {
    before: ChatMessage[];
    after: ChatMessage[];
  };
  connectorMultiAgentChatInputs: {
    before: string;
    after: string;
  };

  // Действия для ConnectorPage конфигурации
  setConnectorWorkers: (workers: WorkerAgentConfig[]) => void;
  setConnectorCoordinatorPrompt: (prompt: string) => void;
  setConnectorCoordinatorModel: (model: string) => void;
  setConnectorCoordinatorTemperature: (temperature: number) => void;
  setConnectorCoordinatorDescription: (description: string) => void;
  setConnectorCoordinatorReminderPrompt: (value: string) => void;
  setConnectorCoordinatorName: (name: string) => void;
  setConnectorCoordinatorBackendId: (value?: string) => void;
  setConnectorCoordinatorAgentId: (value?: string) => void;
  setConnectorReminderPromptPosition: (value: 'before' | 'after') => void;
  setConnectorConfigLabel: (label?: string) => void;
  setConnectorMetaPrompts: (metaPrompts?: {
    CORE_PROMPT?: string;
    MASTER_SUFFIX?: string;
    ASYNC_SUFFIX?: string;
    SYNC_EXTRA?: string;
    RAG_PROMPT?: string;
    LTM_PROMPT?: string;
    MODERATION_PROMPT?: string;
    DIARY_WRITE_PROMPT?: string;
    DIARY_READ_PROMPT?: string;
    PORTRAIT_READ_PROMPT?: string;
    PORTRAIT_WRITE_PROMPT?: string;
    FINAL_STEP_PROMPT?: string;
  }) => void;

  // Действия для Connector Multi-Agent
  setConnectorMultiAgentOptimizedPrompts: (prompts: string[]) => void;
  setConnectorMultiAgentIterationResults: (
    results: Array<{
      prompt: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      iteration: number;
      seq?: number;
      temperature?: number;
      'master-agent'?: {
        name: string;
        prompt: string;
        temperature: number;
        model_name: string;
        description: string;
        reminder_prompt?: string;
      };
      'worker-agents'?: Array<{
        name: string;
        prompt: string;
        temperature: number;
        model_name: string;
        description: string;
        mode?: 'synchronous' | 'asynchronous';
        reminder_prompt?: string;
      }>;
    }>
  ) => void;
  setConnectorMultiAgentIsOptimized: (isOptimized: boolean) => void;
  setConnectorMultiAgentSelectedIterationIndex: (index: number) => void;
  setConnectorMultiAgentSelectedPrompt: (prompt: string) => void;
  setConnectorMultiAgentSelectedPromptIndex: (index: string) => void;
  setConnectorMultiAgentGraphSliderValue: (value: number) => void;
  setConnectorMultiAgentIsOptimizing: (isOptimizing: boolean) => void;
  setConnectorMultiAgentOptimizationProgress: (progress: number) => void;
  setConnectorMultiAgentProgressStartTime: (time: number | null) => void;
  setConnectorMultiAgentCurrentProgressStage: (stage: number) => void;
  setConnectorMultiAgentExpectedIterationTime: (time: number) => void;
  addConnectorMultiAgentIterationResult: (result: {
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number;
    temperature?: number;
    'master-agent'?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    'worker-agents'?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      mode?: 'synchronous' | 'asynchronous';
      reminder_prompt?: string;
    }>;
    meta_prompts?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
    metaPrompts?: {
      CORE_PROMPT?: string;
      MASTER_SUFFIX?: string;
      ASYNC_SUFFIX?: string;
      SYNC_EXTRA?: string;
      RAG_PROMPT?: string;
      LTM_PROMPT?: string;
      MODERATION_PROMPT?: string;
      DIARY_WRITE_PROMPT?: string;
      DIARY_READ_PROMPT?: string;
      PORTRAIT_READ_PROMPT?: string;
      PORTRAIT_WRITE_PROMPT?: string;
      FINAL_STEP_PROMPT?: string;
    };
  }) => void;
  processConnectorMultiAgentOptimizationResult: (result: {
    prompt?: string;
    prompts?: string[];
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    totalIterations?: number;
    temperature?: number;
    'master-agent'?: {
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    };
    'worker-agents'?: Array<{
      name: string;
      prompt: string;
      temperature: number;
      model_name: string;
      description: string;
      reminder_prompt?: string;
    }>;
  }) => void;
  setConnectorMultiAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) => void;
  setConnectorMultiAgentChatInputs: (inputs: { before: string; after: string }) => void;
  setConnectorMultiAgentChatInput: (type: 'before' | 'after', value: string) => void;
  addConnectorMultiAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) => void;
  clearConnectorMultiAgentChat: () => void;
}

export const createConnectorSlice: StateCreator<
  OptimizationState,
  [['zustand/persist', unknown]],
  [],
  ConnectorSlice
> = (set, get) => ({
  // Конфигурация для ConnectorPage по умолчанию
  connectorWorkers: cloneWorkers(INITIAL_WORKERS),
  connectorCoordinatorPrompt: 'You are a helpful assistant.',
  connectorCoordinatorModel: 'gpt-4.1-mini',
  connectorCoordinatorTemperature: 0.7,
  connectorCoordinatorDescription: DEFAULT_COORDINATOR_DESCRIPTION,
  connectorCoordinatorReminderPrompt: '',
  connectorCoordinatorName: 'Coordinator',
  connectorCoordinatorBackendId: undefined,
  connectorCoordinatorAgentId: undefined,
  connectorReminderPromptPosition: 'before',
  connectorConfigLabel: undefined,
  connectorMetaPrompts: undefined,

  // Мультиагентные результаты оптимизации для ConnectorPage
  connectorMultiAgentOptimizedPrompts: [],
  connectorMultiAgentIterationResults: [],
  connectorMultiAgentIsOptimized: false,
  connectorMultiAgentSelectedIterationIndex: 0,
  connectorMultiAgentSelectedPrompt: '',
  connectorMultiAgentSelectedPromptIndex: '',
  connectorMultiAgentGraphSliderValue: 0,
  connectorMultiAgentIsOptimizing: false,
  connectorMultiAgentOptimizationProgress: 0,
  connectorMultiAgentProgressStartTime: null,
  connectorMultiAgentCurrentProgressStage: 0,
  connectorMultiAgentExpectedIterationTime: 60,

  // Чат для Connector Multi-Agent
  connectorMultiAgentChatMessages: {
    before: [],
    after: [],
  },
  connectorMultiAgentChatInputs: {
    before: '',
    after: '',
  },

  // Действия для ConnectorPage конфигурации
  setConnectorWorkers: (workers: WorkerAgentConfig[]) =>
    set({
      connectorWorkers: cloneWorkers(workers.map((w, i) => ({ ...w, id: normalizeWorkerIdString(w.id, i) }))),
    }),
  setConnectorCoordinatorPrompt: (prompt: string) => set({ connectorCoordinatorPrompt: prompt }),
  setConnectorCoordinatorModel: (model: string) => set({ connectorCoordinatorModel: model }),
  setConnectorCoordinatorTemperature: (temperature: number) =>
    set({ connectorCoordinatorTemperature: Math.min(temperature, 1) }),
  setConnectorCoordinatorDescription: (description: string) => set({ connectorCoordinatorDescription: description }),
  setConnectorCoordinatorReminderPrompt: (value: string) => set({ connectorCoordinatorReminderPrompt: value }),
  setConnectorCoordinatorName: (name: string) => set({ connectorCoordinatorName: name.trim() || 'Coordinator' }),
  setConnectorCoordinatorBackendId: (value?: string) =>
    set({ connectorCoordinatorBackendId: value?.trim() || undefined }),
  setConnectorCoordinatorAgentId: (value?: string) => set({ connectorCoordinatorAgentId: value?.trim() || undefined }),
  setConnectorReminderPromptPosition: (value: 'before' | 'after') => set({ connectorReminderPromptPosition: value }),
  setConnectorConfigLabel: (label?: string) => set({ connectorConfigLabel: label }),
  setConnectorMetaPrompts: (metaPrompts?: {
    CORE_PROMPT?: string;
    MASTER_SUFFIX?: string;
    ASYNC_SUFFIX?: string;
    SYNC_EXTRA?: string;
    RAG_PROMPT?: string;
    LTM_PROMPT?: string;
    MODERATION_PROMPT?: string;
    DIARY_WRITE_PROMPT?: string;
    DIARY_READ_PROMPT?: string;
    PORTRAIT_READ_PROMPT?: string;
    PORTRAIT_WRITE_PROMPT?: string;
    FINAL_STEP_PROMPT?: string;
  }) => set({ connectorMetaPrompts: metaPrompts }),

  // Действия для Connector Multi-Agent
  setConnectorMultiAgentOptimizedPrompts: (prompts: string[]) => set({ connectorMultiAgentOptimizedPrompts: prompts }),
  setConnectorMultiAgentIterationResults: (results) => set({ connectorMultiAgentIterationResults: results }),
  setConnectorMultiAgentIsOptimized: (isOptimized: boolean) => set({ connectorMultiAgentIsOptimized: isOptimized }),
  setConnectorMultiAgentSelectedIterationIndex: (index: number) =>
    set({ connectorMultiAgentSelectedIterationIndex: index }),
  setConnectorMultiAgentSelectedPrompt: (prompt: string) => set({ connectorMultiAgentSelectedPrompt: prompt }),
  setConnectorMultiAgentSelectedPromptIndex: (index: string) => set({ connectorMultiAgentSelectedPromptIndex: index }),
  setConnectorMultiAgentGraphSliderValue: (value: number) => set({ connectorMultiAgentGraphSliderValue: value }),
  setConnectorMultiAgentIsOptimizing: (isOptimizing: boolean) => set({ connectorMultiAgentIsOptimizing: isOptimizing }),
  setConnectorMultiAgentOptimizationProgress: (progress: number) =>
    set((state: OptimizationState) => {
      const incoming = Math.max(0, Math.min(100, progress));
      const current =
        typeof state.connectorMultiAgentOptimizationProgress === 'number'
          ? state.connectorMultiAgentOptimizationProgress
          : 0;
      const next = incoming === 0 ? 0 : Math.max(current, incoming);
      if (next !== current) {
        return { connectorMultiAgentOptimizationProgress: next };
      }
      return {};
    }),
  setConnectorMultiAgentProgressStartTime: (time: number | null) => set({ connectorMultiAgentProgressStartTime: time }),
  setConnectorMultiAgentCurrentProgressStage: (stage: number) =>
    set({ connectorMultiAgentCurrentProgressStage: stage }),
  setConnectorMultiAgentExpectedIterationTime: (time: number) =>
    set({ connectorMultiAgentExpectedIterationTime: time }),
  addConnectorMultiAgentIterationResult: (result) =>
    set((state: OptimizationState) => {
      const seq = typeof result.seq === 'number' ? result.seq : undefined;
      const incomingSeqOrIter = seq !== undefined ? seq : result.iteration;

      // Deduplicate by seq/iteration
      if (typeof incomingSeqOrIter === 'number') {
        const alreadyExists = state.connectorMultiAgentIterationResults.some((r) => {
          const existingSeqOrIter = typeof r.seq === 'number' ? r.seq : r.iteration;
          return existingSeqOrIter === incomingSeqOrIter;
        });
        if (alreadyExists) {
          return {};
        }
      }

      // seq: 0 - baseline evaluation
      if (seq === 0) {
        return {
          connectorMultiAgentIterationResults: [
            ...state.connectorMultiAgentIterationResults,
            {
              ...result,
              seq: 0,
              iteration: 0,
            },
          ],
        };
      }

      // seq: 1, 2, 3... - optimized configurations
      const updatedResult = {
        ...result,
        seq: seq !== undefined ? seq : result.iteration,
        iteration: seq !== undefined ? seq : result.iteration,
      };

      const masterPrompt = updatedResult['master-agent']?.prompt || updatedResult.prompt;
      const currentOptimizedCount = state.connectorMultiAgentOptimizedPrompts.length;
      const maxIterations = state.multiAgentIterations || 4;

      if (currentOptimizedCount < maxIterations && masterPrompt) {
        return {
          connectorMultiAgentIterationResults: [...state.connectorMultiAgentIterationResults, updatedResult],
          connectorMultiAgentOptimizedPrompts: [...state.connectorMultiAgentOptimizedPrompts, masterPrompt],
        };
      }

      return {
        connectorMultiAgentIterationResults: [...state.connectorMultiAgentIterationResults, updatedResult],
      };
    }),
  processConnectorMultiAgentOptimizationResult: (result) =>
    set((state: OptimizationState) => {
      let updatedPrompts = state.connectorMultiAgentOptimizedPrompts;
      if (result.prompts && result.prompts.length > 0) {
        updatedPrompts = result.prompts;
      } else if (result.prompt) {
        const lastResult =
          state.connectorMultiAgentIterationResults[state.connectorMultiAgentIterationResults.length - 1];
        if (lastResult && lastResult.seq !== 0) {
          updatedPrompts = [...state.connectorMultiAgentOptimizedPrompts, result.prompt];
        }
      }

      return {
        connectorMultiAgentOptimizedPrompts: updatedPrompts,
      };
    }),
  setConnectorMultiAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) =>
    set({ connectorMultiAgentChatMessages: messages }),
  setConnectorMultiAgentChatInputs: (inputs: { before: string; after: string }) =>
    set({ connectorMultiAgentChatInputs: inputs }),
  setConnectorMultiAgentChatInput: (type: 'before' | 'after', value: string) =>
    set((state) => ({
      connectorMultiAgentChatInputs: {
        ...state.connectorMultiAgentChatInputs,
        [type]: value,
      },
    })),
  addConnectorMultiAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) =>
    set((state) => ({
      connectorMultiAgentChatMessages: {
        ...state.connectorMultiAgentChatMessages,
        [type]: [...state.connectorMultiAgentChatMessages[type], message],
      },
    })),
  clearConnectorMultiAgentChat: () =>
    set({
      connectorMultiAgentChatMessages: { before: [], after: [] },
      connectorMultiAgentChatInputs: { before: '', after: '' },
    }),
});
